<template>
  <TransitionRoot appear :show="searchStore.isSearchModalOpen" as="template">
    <Dialog 
      as="div" 
      class="relative z-[57]"
      @close="handleClose"
    >
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-secondary-900/50 dark:bg-secondary-900/80" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto pt-20">
        <div class="flex justify-center p-4">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 -translate-y-4"
            enter-to="opacity-100 translate-y-0"
            leave="duration-200 ease-in"
            leave-from="opacity-100 translate-y-0"
            leave-to="opacity-0 -translate-y-4"
          >
            <DialogPanel 
              class="w-full max-w-3xl transform overflow-hidden rounded-xl bg-white dark:bg-secondary-800 shadow-2xl transition-all border border-secondary-200 dark:border-secondary-700"
            >
              <div class="relative">
                <!-- Search Input -->
                <div class="p-4 border-b border-secondary-200 dark:border-secondary-700">
                  <div class="relative">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MagnifyingGlassIcon 
                        :class="[
                          'h-5 w-5',
                          searchStore.isLoading ? 'text-primary-500' : 'text-secondary-400'
                        ]"
                      />
                    </div>
                    <input
                      ref="searchInput"
                      v-model.trim="searchStore.searchQuery"
                      @input="handleSearch"
                      type="text"
                      :placeholder="t('dashboard.header.searchModal.placeholder')"
                      class="w-full pl-10 pr-3 py-2 border-0 rounded-md bg-white dark:bg-secondary-900 text-secondary-900 dark:text-white placeholder:text-secondary-400 focus:ring-2 focus:ring-primary-500 focus:ring-offset-0"
                    />
                    <div v-if="searchStore.isLoading" class="absolute inset-y-0 right-0 pr-3 flex items-center">
                      <div class="animate-spin text-primary-500">
                        <ArrowPathIcon class="h-5 w-5" />
                      </div>
                    </div>
                  </div>

                  <!-- Index Filter Chips -->
                  <div class="mt-3 flex flex-wrap items-center gap-2 border-t border-secondary-200 dark:border-secondary-700 pt-3">
                    <button
                      @click="handleToggleAll"
                      :class="[
                        'px-2.5 py-1 text-xs font-medium rounded-full border transition-all duration-200',
                        allIndicesSelected
                          ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-300 dark:border-primary-700 text-primary-700 dark:text-primary-300'
                          : 'bg-white dark:bg-secondary-800 border-secondary-300 dark:border-secondary-600 text-secondary-600 dark:text-secondary-400 hover:border-primary-300 dark:hover:border-primary-600'
                      ]"
                    >
                      {{ t('dashboard.header.searchModal.filters.all') }}
                    </button>
                    <button
                      v-for="(indexValue, indexKey) in ALGOLIA_INDICES"
                      :key="indexKey"
                      @click="handleFilterToggle(indexValue)"
                      :class="[
                        'px-2.5 py-1 text-xs font-medium rounded-full border transition-all duration-200',
                        searchStore.selectedIndices.includes(indexValue)
                          ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-300 dark:border-primary-700 text-primary-700 dark:text-primary-300'
                          : 'bg-white dark:bg-secondary-800 border-secondary-300 dark:border-secondary-600 text-secondary-600 dark:text-secondary-400 hover:border-primary-300 dark:hover:border-primary-600'
                      ]"
                    >
                      {{ t(`dashboard.header.searchModal.filters.${indexKey}`) }}
                    </button>
                  </div>

                  <!-- Results Limit Selector -->
                  <div class="mt-3 flex items-center justify-between border-t border-secondary-200 dark:border-secondary-700 pt-3">
                    <span class="ml-2 text-sm text-secondary-600 dark:text-secondary-400 flex justify-center items-center gap-1.5">
                      <ListBulletIcon class="h-4 w-4" />
                      <p cla>{{ t('dashboard.header.searchModal.showResults') }}</p>
                    </span>
                    <div class="inline-flex rounded-lg p-0.5 bg-secondary-100 dark:bg-secondary-700/50">
                      <button
                        v-for="limit in [5, 10, 15]"
                        :key="limit"
                        @click="handleLimitChange(limit)"
                        :disabled="searchStore.isLoading"
                        :class="[
                          'px-3 py-1 text-sm font-medium rounded-md transition-all duration-200 relative',
                          searchStore.resultsLimit === limit && !showingAllResults
                            ? 'bg-white dark:bg-secondary-800 text-primary-600 dark:text-primary-400 shadow-sm'
                            : 'text-secondary-600 dark:text-secondary-400 hover:text-primary-600 dark:hover:text-primary-400',
                          searchStore.isLoading ? 'cursor-not-allowed opacity-50' : ''
                        ]"
                      >
                        <span :class="{ 'opacity-0': searchStore.isLoading && searchStore.resultsLimit === limit && !showingAllResults }">
                          {{ limit }}
                        </span>
                        <ArrowPathIcon 
                          v-if="searchStore.isLoading && searchStore.resultsLimit === limit && !showingAllResults"
                          class="animate-spin h-4 w-4 absolute inset-0 m-auto text-primary-500"
                        />
                      </button>
                      <button
                        @click="handleShowAllResults"
                        :disabled="searchStore.isLoading"
                        :class="[
                          'px-3 py-1 text-sm font-medium rounded-md transition-all duration-200 relative',
                          showingAllResults
                            ? 'bg-white dark:bg-secondary-800 text-primary-600 dark:text-primary-400 shadow-sm'
                            : 'text-secondary-600 dark:text-secondary-400 hover:text-primary-600 dark:hover:text-primary-400',
                          searchStore.isLoading ? 'cursor-not-allowed opacity-50' : ''
                        ]"
                      >
                        <span :class="{ 'opacity-0': searchStore.isLoading && showingAllResults }">
                          {{ t('dashboard.header.searchModal.showAll') }}
                        </span>
                        <ArrowPathIcon 
                          v-if="searchStore.isLoading && showingAllResults"
                          class="animate-spin h-4 w-4 absolute inset-0 m-auto text-primary-500"
                        />
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Results -->
                <div class="max-h-[calc(100vh-300px)] overflow-y-auto">
                  <!-- Loading State -->
                  <div 
                    v-if="searchStore.isLoading && searchStore.searchQuery" 
                    class="p-8 text-center"
                  >
                    <div class="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900/20 mb-4">
                      <ArrowPathIcon class="animate-spin h-6 w-6 text-primary-500" />
                    </div>
                    <p class="text-secondary-600 dark:text-secondary-400">{{ t('dashboard.header.searchModal.loading') }}</p>
                  </div>

                  <!-- Results List -->
                  <div v-else-if="searchStore.hasResults" class="divide-y divide-secondary-200 dark:divide-secondary-700">
                    <div v-for="(results, type) in groupedResults" :key="type" class="p-4">
                      <div class="flex items-center justify-between mb-4">
                        <div class="flex items-center gap-3">
                          <div class="flex items-center justify-center w-8 h-8 rounded-lg bg-primary-50 dark:bg-primary-900/20">
                            <component 
                              :is="getIndexIcon(type)" 
                              class="h-4 w-4 text-primary-600 dark:text-primary-400"
                            />
                          </div>
                          <div class="flex items-center gap-2">
                            <h2 class="text-base font-semibold text-secondary-900 dark:text-white capitalize">
                              {{ formatIndexName(type) }}
                            </h2>
                            <span class="text-sm text-secondary-500 dark:text-secondary-400">
                              · {{ searchStore.totalResultsByType[type] || 0 }} {{ t(`dashboard.header.searchModal.results.${(searchStore.totalResultsByType[type] || 0) === 1 ? 'count' : 'counts'}`) }}
                            </span>
                          </div>
                        </div>
                        <div v-if="!showingAllResults && (searchStore.totalResultsByType[type] || 0) > results.length" class="text-xs text-secondary-500 dark:text-secondary-400 bg-secondary-50 dark:bg-secondary-800/50 px-2 py-1 rounded-md flex items-center">
                          <ListBulletIcon class="h-3.5 w-3.5 text-primary-500 mr-1.5" />
                          {{ t('dashboard.header.searchModal.showingLimited', { displayed: results.length, total: searchStore.totalResultsByType[type] || 0 }) }}
                        </div>
                      </div>
                      <div class="grid gap-3">
                        <div 
                          v-for="result in results" 
                          :key="result.objectID" 
                          class="group relative p-3 rounded-lg border border-secondary-200 dark:border-secondary-700 hover:border-primary-500 dark:hover:border-primary-400 bg-white dark:bg-secondary-800 hover:shadow-md transition-all duration-200 cursor-pointer"
                          @click="handleResultClick(result)"
                        >
                          <div class="flex items-start justify-between gap-4">
                            <div class="flex-1 min-w-0">
                              <!-- Item Result Card -->
                              <template v-if="isItemResult(result.type)">
                                <div class="flex items-center gap-2 mb-2">
                                  <div class="flex-1">
                                    <div class="flex items-center gap-2">
                                      <span class="font-medium text-sm text-secondary-900 dark:text-white truncate">
                                        {{ result.sku || result.description || result.objectID }}
                                      </span>
                                    </div>
                                    <div v-if="result.sku && result.description" class="mt-1 text-xs text-secondary-500 dark:text-secondary-400 truncate">
                                      {{ result.description }}
                                    </div>
                                    <div class="mt-1.5 flex items-center text-xs text-secondary-500 dark:text-secondary-400">
                                      <component
                                        :is="result.type === 'SHIPMENT_ITEMS' ? Ship : PackageSearch"
                                        class="h-3.5 w-3.5 mr-1.5 flex-shrink-0 text-primary-500"
                                      />
                                      <span v-if="result.type === 'SHIPMENT_ITEMS'">
                                        {{ t('dashboard.header.searchModal.itemParent.shipment') }}: {{ result.idNumber || result.shipmentId }}
                                      </span>
                                      <span v-else>
                                        {{ t('dashboard.header.searchModal.itemParent.purchaseOrder') }}: {{ result.purchaseOrderId }}
                                      </span>
                                    </div>

                                    <!-- Highlighted fields -->
                                    <div class="mt-2 space-y-1.5">
                                      <template v-for="(highlight, field) in getHighlightedFields(result)" :key="field">
                                        <div v-if="highlight.matchLevel !== 'none' && searchStore.searchQuery.length >= 3" class="text-xs text-secondary-500 dark:text-secondary-400">
                                          <span class="font-medium">{{ formatFieldName(field) }}:</span>
                                          <span class="ml-1">
                                            <HighlightedText
                                              :content="getFieldValue(result, field)"
                                              :highlight-result="highlight"
                                            />
                                          </span>
                                        </div>
                                      </template>
                                    </div>
                                  </div>
                                </div>
                              </template>

                              <!-- Standard Result Card -->
                              <template v-else>
                                <div class="flex items-center gap-2 mb-2">
                                  <div class="flex-1">
                                    <div class="flex items-center gap-2">
                                      <span class="font-medium text-sm text-secondary-900 dark:text-white truncate">
                                        {{ result.idNumber || result.mbl || result.booking }}
                                      </span>
                                      <span
                                        v-if="result.status"
                                        :class="[
                                          'px-2 py-0.5 text-xs rounded-full font-medium',
                                          getStatusClass(result.status)
                                        ]"
                                      >
                                        {{ result.status }}
                                      </span>
                                    </div>

                                    <div class="mt-2 space-y-1.5">
                                      <template v-for="(highlight, field) in getHighlightedFields(result)" :key="field">
                                        <div v-if="highlight.matchLevel !== 'none' && searchStore.searchQuery.length >= 3" class="text-xs text-secondary-500 dark:text-secondary-400">
                                          <span class="font-medium">{{ formatFieldName(field) }}:</span>
                                          <span class="ml-1">
                                            <HighlightedText
                                              :content="getFieldValue(result, field)"
                                              :highlight-result="highlight"
                                            />
                                          </span>
                                        </div>
                                      </template>
                                    </div>
                                  </div>
                                </div>

                                <div class="grid grid-cols-2 gap-3 text-xs">
                                  <div class="space-y-1">
                                    <div v-if="result.transportMode" class="flex items-center text-secondary-600 dark:text-secondary-400">
                                      <component
                                        :is="getTransportModeIcon(result.transportMode)"
                                        class="h-4 w-4 mr-1.5 flex-shrink-0"
                                      />
                                      {{ result.transportMode }}
                                    </div>
                                    <div v-if="getTransportDocument(result)" class="flex items-center text-secondary-600 dark:text-secondary-400">
                                      <FileText
                                        class="h-4 w-4 mr-1.5 flex-shrink-0"
                                      />
                                      {{ getTransportDocument(result) }}
                                    </div>
                                  </div>
                                  <div class="space-y-1">
                                    <div v-if="result.internationalCarrier || result['3pl']" class="flex items-center text-secondary-600 dark:text-secondary-400">
                                      <Building2
                                        class="h-4 w-4 mr-1.5 flex-shrink-0"
                                      />
                                      {{ result.internationalCarrier || result['3pl'] }}
                                    </div>
                                  </div>
                                </div>
                              </template>
                            </div>

                            <!-- Right Arrow Icon -->
                            <div class="text-secondary-400 dark:text-secondary-500 group-hover:text-primary-500 transition-colors">
                              <ChevronRightIcon class="h-5 w-5" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- No Results State -->
                  <div 
                    v-else-if="searchStore.searchQuery && !searchStore.isLoading" 
                    class="p-8 text-center"
                  >
                    <p class="text-secondary-600 dark:text-secondary-400">{{ t('dashboard.header.searchModal.noResults') }}</p>
                  </div>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { 
  MagnifyingGlassIcon, 
  ArrowPathIcon, 
  ChevronRightIcon,
  ListBulletIcon
} from '@heroicons/vue/24/outline';
import { 
  Ship, 
  PackageSearch, 
  Truck, 
  Building2, 
  Calculator,
  Plane,
  Container,
  FileText,
  Package,
  Warehouse
} from 'lucide-vue-next';
import { useSearchStore } from '@/stores/search';
import HighlightedText from './HighlightedText.vue';
import { ALGOLIA_INDICES } from '@/config/algolia';
import debounce from 'lodash/debounce';
import get from 'lodash/get';

const router = useRouter();
const searchStore = useSearchStore();
const searchInput = ref<HTMLInputElement | null>(null);
const currentSearchValue = ref('');
const showingAllResults = ref(false);
const { t } = useI18n();

const ITEM_TYPES = ['SHIPMENT_ITEMS', 'PURCHASE_ORDERS_ITEMS'];

const allIndicesSelected = computed(() => {
  return searchStore.selectedIndices.length === Object.values(ALGOLIA_INDICES).length;
});

const isItemResult = (type: string) => ITEM_TYPES.includes(type);

// Computed properties
const groupedResults = computed(() => {
  const groups: Record<string, any[]> = {};
  searchStore.searchResults.forEach(result => {
    if (!groups[result.type]) {
      groups[result.type] = [];
    }
    groups[result.type].push(result);
  });
  
  return groups;
});

// Methods
const handleSearch = debounce(async () => {
  if (searchStore.searchQuery !== currentSearchValue.value) {
    currentSearchValue.value = searchStore.searchQuery;
    showingAllResults.value = false;
    await searchStore.performSearch(searchStore.searchQuery);
  }
}, 300);

const handleClose = () => {
  searchStore.closeSearchModal();
  showingAllResults.value = false;
};

const handleResultClick = (result: any) => {
  navigateToResult(result);
  searchStore.handleResultSelection();
  showingAllResults.value = false;
};

const handleLimitChange = async (limit: number) => {
  if (searchStore.isLoading) return;
  
  // Guardar el valor actual de búsqueda
  const previousQuery = currentSearchValue.value;
  
  // Cambiar el límite de resultados y desactivar "Ver todos"
  searchStore.setResultsLimit(limit);
  showingAllResults.value = false;
  
  // Restaurar el valor de búsqueda y ejecutar la búsqueda nuevamente
  if (previousQuery) {
    searchStore.searchQuery = previousQuery;
    await searchStore.performSearch(previousQuery);
  }
};

const handleToggleAll = async () => {
  if (allIndicesSelected.value) {
    searchStore.deselectAllIndices();
  } else {
    searchStore.selectAllIndices();
  }
  if (currentSearchValue.value) {
    await searchStore.performSearch(currentSearchValue.value, showingAllResults.value);
  }
};

const handleFilterToggle = async (indexValue: string) => {
  searchStore.toggleIndex(indexValue);
  if (currentSearchValue.value) {
    await searchStore.performSearch(currentSearchValue.value, showingAllResults.value);
  }
};

const handleShowAllResults = async () => {
  if (searchStore.isLoading) return;
  
  // Guardar el valor actual de búsqueda
  const previousQuery = currentSearchValue.value;
  
  // Activar el estado de "Ver todos"
  showingAllResults.value = true;
  
  // Restaurar el valor de búsqueda y ejecutar la búsqueda nuevamente
  if (previousQuery) {
    searchStore.searchQuery = previousQuery;
    await searchStore.performSearch(previousQuery, true);
  }
};

const navigateToResult = (result: any) => {
  const routes: Record<string, string> = {
    'SHIPMENTS': `/dashboard/tracking-results?id=${result.objectID}`,
    'PURCHASE_ORDERS': `/dashboard/purchase-orders/${result.objectID}`,
    'DELIVERIES': `/deliveries/${result.objectID}`,
    'WAREHOUSES': `/warehouses/${result.objectID}`,
    'QUOTES': `/quotes/${result.objectID}`,
    'SHIPMENT_ITEMS': `/dashboard/tracking-results?id=${result.shipmentId}`,
    'PURCHASE_ORDERS_ITEMS': `/dashboard/purchase-orders/${result.purchaseOrderId}`
  };

  const route = routes[result.type];
  if (route) {
    router.push(route);
  }
};

// Utility functions
const formatIndexName = (type: string) => {
  const typeMappings: Record<string, string> = {
    'SHIPMENTS': t('dashboard.header.searchModal.types.shipments'),
    'PURCHASE_ORDERS': t('dashboard.header.searchModal.types.purchaseOrders'),
    'DELIVERIES': t('dashboard.header.searchModal.types.deliveries'),
    'WAREHOUSES': t('dashboard.header.searchModal.types.warehouses'),
    'QUOTES': t('dashboard.header.searchModal.types.quotes'),
    'SHIPMENT_ITEMS': t('dashboard.header.searchModal.types.shipmentItems'),
    'PURCHASE_ORDERS_ITEMS': t('dashboard.header.searchModal.types.purchaseOrdersItems')
  };

  return typeMappings[type] || type.toLowerCase();
};

const getIndexIcon = (type: string) => {
  // Verificar qué tipo de índice está llegando
  
  // Mapeo directo de tipos a iconos
  if (type === 'SHIPMENTS') return Ship;
  if (type === 'PURCHASE_ORDERS') return PackageSearch;
  if (type === 'DELIVERIES') return Truck;
  if (type === 'WAREHOUSES') return Warehouse;
  if (type === 'QUOTES') return Calculator;
  if (type === 'SHIPMENT_ITEMS') return Package;
  if (type === 'PURCHASE_ORDERS_ITEMS') return Package;

  return MagnifyingGlassIcon;
};

const getTransportModeIcon = (mode: string) => {
  const modeMap = {
    [t('dashboard.header.searchModal.transportModes.groundTransport')]: Truck,
    [t('dashboard.header.searchModal.transportModes.airTransport')]: Plane,
    [t('dashboard.header.searchModal.transportModes.oceanTransport')]: Ship,
    [t('dashboard.header.searchModal.transportModes.container')]: Container,
    [t('dashboard.header.searchModal.transportModes.fullTruck')]: Truck,
    [t('dashboard.header.searchModal.transportModes.partialTruck')]: Truck,
    [t('dashboard.header.searchModal.transportModes.air')]: Plane,
    [t('dashboard.header.searchModal.transportModes.ocean')]: Ship,
    [t('dashboard.header.searchModal.transportModes.consolidated')]: Package
  };
  return modeMap[mode] || Package;
};

const getTransportDocument = (result: any) => {
  if (result.booking) return `${t('dashboard.header.searchModal.documents.booking')}: ${result.booking}`;
  if (result.mbl) return `${t('dashboard.header.searchModal.documents.mbl')}: ${result.mbl}`;
  if (result.hbl) return `${t('dashboard.header.searchModal.documents.hbl')}: ${result.hbl}`;
  if (result.mawb) return `${t('dashboard.header.searchModal.documents.mawb')}: ${result.mawb}`;
  return null;
};

const getStatusClass = (status: string) => {
  const statusClasses = {
    [t('dashboard.header.searchModal.status.bookingRequest')]: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300',
    [t('dashboard.header.searchModal.status.pickedUp')]: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    [t('dashboard.header.searchModal.status.departed')]: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
    [t('dashboard.header.searchModal.status.arrived')]: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    [t('dashboard.header.searchModal.status.customs')]: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
    [t('dashboard.header.searchModal.status.cleared')]: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
    [t('dashboard.header.searchModal.status.delivered')]: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300',
    [t('dashboard.header.searchModal.status.completed')]: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300'
  } as Record<string, string>;

  return statusClasses[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
};

const getHighlightedFields = (result: any) => {
  if (!result._highlightResult) return {};
  
  const highlightedFields: Record<string, any> = {};
  
  // Lista de campos a excluir
  const excludedFields = [
    '_seconds',
    '_nanoseconds',
    'timezone',
    'createdAt',
    'updatedAt',
    'deletedAt',
    'shipmentDate',
    'shipmentCompletedDate',
    'eta',
    'ata',
    'atd',
    'requestedPickupDate',
    'requestedDeliveryDate',
    'deliveryDate',
    'milestones.ata',
    'milestones.atd',
    'milestones.deliveryDate',
    'milestones.requestedPickupDate',
    'milestones.requestedDeliveryDate'
  ];

  // Función para verificar si un campo debe ser excluido
  const shouldExcludeField = (path: string) => {
    return excludedFields.some(excludedField => 
      path.toLowerCase().includes(excludedField.toLowerCase()) ||
      path.toLowerCase().endsWith('_at') ||
      path.toLowerCase().endsWith('date')
    );
  };
  
  // Función para determinar si una clave es un índice de array
  const isArrayIndex = (key: string) => {
    return /^\d+$/.test(key);
  };
  
  const processHighlights = (highlights: any, path = '', originalObject = result) => {
    Object.entries(highlights).forEach(([key, value]: [string, any]) => {
      // Determinar si estamos procesando un índice de array
      const isIndex = isArrayIndex(key);
      
      // Construir la ruta actual
      let currentPath = path;
      if (isIndex) {
        // Para índices de array, mantener la misma ruta pero guardar el índice para acceder al valor
        currentPath = path;
      } else {
        // Para propiedades normales, añadir la clave a la ruta
        currentPath = path ? `${path}.${key}` : key;
      }
      
      // Saltar el campo si debe ser excluido
      if (shouldExcludeField(currentPath)) {
        return;
      }
      
      if (value && typeof value === 'object') {
        if ('matchLevel' in value && value.matchLevel !== 'none') {
          // Si es un índice de array, modificar la clave para incluir información sobre el elemento
          if (isIndex) {
            // Obtener el valor original para proporcionar contexto
            const arrayPath = path.split('.');
            const arrayObj = arrayPath.reduce((obj, p) => obj && obj[p], originalObject);
            const arrayItem = arrayObj && arrayObj[parseInt(key)];
            
            // Crear una clave descriptiva que incluya el nombre del array y alguna información del elemento
            let descriptiveKey = path;
            
            // Intentar añadir un identificador significativo del elemento
            if (arrayItem && typeof arrayItem === 'object') {
              // Buscar propiedades identificativas comunes
              const identifiers = ['id', 'name', 'title', 'code', 'number', 'type'];
              for (const id of identifiers) {
                if (arrayItem[id]) {
                  descriptiveKey = `${path}[${arrayItem[id]}]`;
                  break;
                }
              }
              
              // Si no se encontró un identificador, usar alguna otra propiedad disponible
              if (descriptiveKey === path) {
                const firstProp = Object.keys(arrayItem).find(k => 
                  typeof arrayItem[k] !== 'object' && 
                  arrayItem[k] !== null && 
                  arrayItem[k] !== undefined
                );
                if (firstProp) {
                  descriptiveKey = `${path}[${firstProp}: ${arrayItem[firstProp]}]`;
                } else {
                  descriptiveKey = `${path}[${key}]`;
                }
              }
            } else {
              // Si el elemento no es un objeto, usar su valor directamente
              descriptiveKey = `${path}[${arrayItem}]`;
            }
            
            highlightedFields[descriptiveKey] = value;
          } else {
            highlightedFields[currentPath] = value;
          }
        } else if (!('matchLevel' in value)) {
          processHighlights(value, currentPath, originalObject);
        }
      }
    });
  };

  processHighlights(result._highlightResult);
  return highlightedFields;
};

const getFieldValue = (obj: any, path: string) => {
  // Manejar rutas especiales para arrays
  if (path.includes('[') && path.includes(']')) {
    // Extraer la ruta base y el índice o descriptor
    const basePath = path.split('[')[0];
    
    // Obtener el array
    const array = get(obj, basePath, []);
    
    // Si no es un array o está vacío, devolver el valor original
    if (!Array.isArray(array) || array.length === 0) {
      return get(obj, path.replace(/\[.*?\]/g, ''), '');
    }
    
    // Intentar encontrar el elemento específico mencionado en el descriptor
    const descriptor = path.match(/\[(.*?)\]/)?.[1];
    if (descriptor) {
      // Si el descriptor es un número, usarlo como índice
      if (/^\d+$/.test(descriptor)) {
        return array[parseInt(descriptor)] || '';
      }
      
      // Si el descriptor contiene un valor específico (ej: "name: John")
      if (descriptor.includes(':')) {
        const [propName, propValue] = descriptor.split(':').map(s => s.trim());
        return array.find(item => 
          item && typeof item === 'object' && 
          String(item[propName]) === propValue
        ) || '';
      }
      
      // Si el descriptor es un valor de propiedad (ej: "John")
      return array.find(item => 
        item && typeof item === 'object' && 
        Object.values(item).some(v => String(v) === descriptor)
      ) || array.find(item => String(item) === descriptor) || '';
    }
    
    return array[0] || '';
  }
  
  return get(obj, path, '');
};

const formatFieldName = (field: string) => {

  // Manejar campos de array con descriptores
  if (field.includes('[') && field.includes(']')) {
    const basePath = field.split('[')[0];
    const descriptor = field.match(/\[(.*?)\]/)?.[1] || '';
    
    // Obtener el nombre formateado para la ruta base
    const baseFieldName = formatFieldName(basePath);
    
    // Si el descriptor parece ser solo un índice numérico, no incluirlo
    if (/^\d+$/.test(descriptor)) {
      return `${baseFieldName} (element)`;
    }
    
    // Si el descriptor contiene ":", formatear como "propiedad: valor"
    if (descriptor.includes(':')) {
      const [propName, propValue] = descriptor.split(':').map(s => s.trim());
      return `${baseFieldName} (${propName}: ${propValue})`;
    }
    
    // En otros casos, incluir el descriptor
    return `${baseFieldName} (${descriptor})`;
  }
  
  const fieldMappings: Record<string, string> = {
    'idNumber': 'ID',
    'shipmentNumber': 'Número de embarque',
    'customerReference': 'Referencia',
    'description': 'Descripción',
    'origin': 'Origen',
    'destination': 'Destino',
    'createdBy': 'Creado por',
    'updatedBy.name': 'Actualizado por',
    'transportMode': 'Modo de transporte',
    'deliveryMode': 'Modo de entrega',
    'vessel': 'Buque',
    'container': 'Contenedor',
    'booking': 'Reserva',
    'hawb': 'HAWB',
    'mawb': 'MAWB',
    'hbl': 'HBL',
    'mbl': 'MBL',
    'internationalCarrier': 'Transportista internacional',
    'lastMileCarrier': 'Transportista última milla',
    '3pl': '3PL',
    'incoterm': 'Incoterm',
    'incotermLocation': 'Ubicación Incoterm',
    'containers': 'Contenedores',
    'documents': 'Documentos',
    'items': 'Artículos',
    'packages': 'Paquetes',
    'products': 'Productos',
    'references': 'Referencias',
    'comments': 'Comentarios',
    'notes': 'Notas',
    'parties': 'Participantes',
    'milestones': 'Hitos'
  };

  return fieldMappings[field] || field
    .split('.')
    .pop()!
    .split(/(?=[A-Z])|_/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

watch(() => searchStore.isSearchModalOpen, (isOpen) => {
  if (isOpen) {
    currentSearchValue.value = '';
    showingAllResults.value = false;
    nextTick(() => {
      searchInput.value?.focus();
    });
  }
});
</script>
